import React from 'react';
import {
	createBrowserRouter,
	createRoutesFromChildren,
	matchRoutes,
	Outlet,
	RouterProvider,
	useLocation,
	useNavigationType,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools'
import * as Sentry from "@sentry/react";

import { AuthProvider } from './restapi/AuthProvider';
import { PageHeader } from './components/common/PageHeader';
import { PageFooter } from './components/common/PageFooter';
import { PageHome } from './components/home/PageHome';
import { PageSection } from './components/section/PageSection';
import { PagePreview } from './components/preview/PagePreview';
import { ConfirmContainer } from './components/common/dialogs/Confirm';


const queryClient = new QueryClient();

function App() {
	return (
		<Sentry.ErrorBoundary fallback={({error}) =>
			<div>
				<h2>An error occurred</h2>
				<p>{String(error)}</p>
				<p>Get back to the <a href="/">homepage</a>.</p>
			</div>
		}>
			<AuthProvider forceLogin>
				<QueryClientProvider client={queryClient}>
					<PageHeader/>

					<main>
						<Outlet/>
					</main>

					<PageFooter/>
					<ConfirmContainer/>
					{!import.meta.env.PROD ? <ReactQueryDevtools initialIsOpen={false}/> : null}
				</QueryClientProvider>
			</AuthProvider>
		</Sentry.ErrorBoundary>
	);
}


// Sentry config
const sentryTracingOptions = {
	useEffect: React.useEffect,
	useLocation,
	useNavigationType,
	createRoutesFromChildren,
	matchRoutes,
};
// noinspection TypeScriptValidateTypes
const tracingIntegration = Sentry.reactRouterV6BrowserTracingIntegration(sentryTracingOptions);

Sentry.init({
	dsn: import.meta.env.VITE_SENTRY_DNS!,
	environment: import.meta.env.VITE_SENTRY_ENV!,
	integrations: [
		tracingIntegration,
	],
	tracesSampleRate: 0.0,
});

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(
	createBrowserRouter,
);


// Router config
const router = sentryCreateBrowserRouter([
	{
		element: <App/>,
		children: [
			{path: "/", Component: PageHome},
			{path: "/section/:id", Component: PageSection},
			{path: "/preview/:id", Component: PagePreview},
			{path: "/:date", Component: PageHome},
		],
	},
]);


export function RoutesWrappedApp() {
	return <RouterProvider router={router}/>;
}
